(() => {
  if (window.CartCrossSell) {
    window.CartCrossSell.scheduleInit();
    document.querySelectorAll("product-info-drawer").forEach((drawer) => {
      window.CartCrossSell.preloadProductDrawerCrossSell(drawer);
    });
    return;
  }

  class CartCrossSell {
  static cache = new Map();
  static loadTokens = new WeakMap();
  static pendingLoads = new WeakSet();
  static initTimer = null;
  static DRAWER_CROSS_SELL_ID = "cart-cross-sell-drawer";

  static init() {
    CartCrossSell.scheduleInit();
  }

  static scheduleInit() {
    clearTimeout(CartCrossSell.initTimer);
    CartCrossSell.initTimer = window.setTimeout(() => {
      document.querySelectorAll(".cart-cross-sell").forEach((container) => {
        CartCrossSell.loadRecommendations(container);
      });
    }, 0);
  }

  static hasRealSlides(container) {
    return Boolean(container?.querySelector(".cart-cross-sell__slide"));
  }

  static getCacheKey(container, productIdsKey) {
    return `${container.dataset.sectionId || "default"}:${productIdsKey}`;
  }

  /* ---------------------------------------------------------------------- */
  /* Slider engine (Swiper)                                                 */
  /* ---------------------------------------------------------------------- */

  static getSliderElement(container) {
    return container?.querySelector(".cart-cross-sell__slider") || null;
  }

  static getWrapper(container) {
    return container?.querySelector(".swiper-wrapper") || null;
  }

  static getSwiper(container) {
    const slider = CartCrossSell.getSliderElement(container);
    return slider?.swiper || null;
  }

  static destroySlider(container) {
    const swiper = CartCrossSell.getSwiper(container);
    if (!swiper) return;

    try {
      // deleteInstance + cleanStyles: fully tears down and clears element.swiper
      swiper.destroy(true, true);
    } catch (e) {
      console.warn("CartCrossSell: swiper destroy failed", e);
    }
  }

  static updateSlider(container) {
    const swiper = CartCrossSell.getSwiper(container);
    if (!swiper) return;
    try {
      swiper.update();
    } catch (e) {
      /* slider was torn down in the meantime — ignore */
    }
  }

  static initSlider(container) {
    if (!container) return;

    const slider = CartCrossSell.getSliderElement(container);
    const wrapper = CartCrossSell.getWrapper(container);
    if (!slider || !wrapper || !wrapper.querySelector(".cart-cross-sell__slide")) {
      return;
    }

    // Swiper bundle is loaded with `defer`; if it hasn't arrived yet fall back
    // to the static CSS grid and retry shortly.
    if (typeof Swiper === "undefined") {
      container.classList.remove("cart-cross-sell--pending-slider");
      container.classList.add("cart-cross-sell--slider-ready");
      CartCrossSell.setLoadingState(container, false);

      const retries = parseInt(container.dataset.swiperRetries || "0", 10);
      if (retries < 20) {
        container.dataset.swiperRetries = String(retries + 1);
        window.setTimeout(() => CartCrossSell.initSlider(container), 150);
      }
      return;
    }
    delete container.dataset.swiperRetries;

    CartCrossSell.destroySlider(container);
    container.classList.add("cart-cross-sell--pending-slider");

    const isPage = container.dataset.context === "page";

    const config = {
      slidesPerView: 2,
      spaceBetween: 15,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: container.querySelector(".cart-cross-sell__arrow--prev"),
        nextEl: container.querySelector(".cart-cross-sell__arrow--next"),
        disabledClass: "cart-cross-sell__arrow--disabled",
      },
    };

    if (isPage) {
      config.breakpoints = {
        1024: { slidesPerView: 4 },
      };
    }

    // eslint-disable-next-line no-new
    new Swiper(slider, config);

    CartCrossSell.finishSliderInit(container);
  }

  static finishSliderInit(container) {
    CartCrossSell.clearSliderInitQueue(container);
    container.classList.remove("cart-cross-sell--pending-slider");
    container.classList.add("cart-cross-sell--slider-ready");
    CartCrossSell.setLoadingState(container, false);

    // Re-measure once layout has settled (drawer transition, reveal, etc.).
    // Swiper's observer/resizeObserver also handle this, this is belt-and-braces.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => CartCrossSell.updateSlider(container));
    }
    window.setTimeout(() => CartCrossSell.updateSlider(container), 250);

    if (container.dataset.context === "product-drawer") {
      container.dispatchEvent(
        new CustomEvent("cross-sell:loaded", { bubbles: true }),
      );
    }
  }

  static clearSliderInitQueue(container) {
    delete container?.dataset.sliderInitQueued;
  }

  static setLoadingState(container, isLoading) {
    container.classList.toggle("cart-cross-sell--loading", isLoading);
    container.toggleAttribute("aria-busy", isLoading);

    container.querySelectorAll(".cart-cross-sell__arrow").forEach((arrow) => {
      arrow.disabled = isLoading;
    });
  }

  static filterSlidesForCart(slides, cartProductIdSet, container) {
    if (container?.dataset.context === "product-drawer") {
      const sourceProductId = container.dataset.productIds;
      return slides.filter((slide) => slide.dataset.productId !== sourceProductId);
    }

    return slides.filter(
      (slide) => !cartProductIdSet.has(slide.dataset.productId),
    );
  }

  static removeCartProductsFromDom(container, cartProductIdSet) {
    container
      .querySelectorAll(".cart-cross-sell__slide")
      .forEach((slide) => {
        if (cartProductIdSet.has(slide.dataset.productId)) {
          slide.remove();
        }
      });
  }

  static cacheSlides(container, productIdsKey, slides) {
    const fragment = document.createDocumentFragment();
    slides.forEach((slide) => fragment.appendChild(slide.cloneNode(true)));
    CartCrossSell.cache.set(
      CartCrossSell.getCacheKey(container, productIdsKey),
      fragment,
    );
  }

  static getCachedSlides(container, productIdsKey) {
    const cached = CartCrossSell.cache.get(
      CartCrossSell.getCacheKey(container, productIdsKey),
    );
    if (!cached) return null;

    return Array.from(cached.childNodes).map((node) => node.cloneNode(true));
  }

  static applySlides(container, slides, productIdsKey) {
    const wrapper = CartCrossSell.getWrapper(container);
    if (!wrapper) return;

    CartCrossSell.destroySlider(container);
    wrapper.innerHTML = "";

    if (slides.length === 0) {
      container.hidden = true;
      container.classList.remove(
        "cart-cross-sell--pending-slider",
        "cart-cross-sell--slider-ready",
      );
      CartCrossSell.setLoadingState(container, false);
      CartCrossSell.cache.delete(
        CartCrossSell.getCacheKey(container, productIdsKey),
      );
      return;
    }

    slides.forEach((slide) => {
      slide.classList.add("swiper-slide");
      wrapper.appendChild(slide);
    });
    container.hidden = false;
    container.dataset.loadedProductIds = productIdsKey;
    container.classList.remove("cart-cross-sell--has-content");
    container.classList.add("cart-cross-sell--pending-slider");
    CartCrossSell.cacheSlides(container, productIdsKey, slides);
    CartCrossSell.initQuickAdd(container);
    CartCrossSell.initSlider(container);
  }

  static preloadProductDrawerCrossSell(drawer) {
    const container = drawer?.querySelector(".cart-cross-sell--product-drawer");
    if (!container) return;

    CartCrossSell.loadRecommendations(container);
  }

  static beforeDrawerReplace() {
    const container = document.getElementById(CartCrossSell.DRAWER_CROSS_SELL_ID);
    if (!container) return null;

    container.remove();
    return container;
  }

  static afterDrawerReplace(preserved, parsedState) {
    if (!preserved) return false;

    if (!parsedState?.items?.length) {
      preserved.hidden = true;
      return false;
    }

    const drawerInner = document.querySelector(".drawer__inner");
    if (!drawerInner) return false;

    drawerInner.querySelector("#cart-cross-sell-drawer")?.remove();

    const drawerContents =
      drawerInner.querySelector("#CartDrawer-CartItems") ||
      drawerInner.querySelector(".drawer__contents");
    const liveRegion = drawerContents?.querySelector("#CartDrawer-LiveRegionText");

    if (liveRegion) {
      liveRegion.before(preserved);
    } else if (drawerContents) {
      drawerContents.appendChild(preserved);
    } else {
      return false;
    }

    const productIds = [
      ...new Set(parsedState.items.map((item) => String(item.product_id))),
    ].join(",");

    CartCrossSell.syncContainer(preserved, productIds);
    return true;
  }

  static syncContainer(container, productIdsKey) {
    if (!container) return;

    const previousKey = container.dataset.productIds || "";
    container.dataset.productIds = productIdsKey;

    if (!productIdsKey) {
      container.hidden = true;
      return;
    }

    const cartProductIdSet = new Set(productIdsKey.split(",").filter(Boolean));
    CartCrossSell.removeCartProductsFromDom(container, cartProductIdSet);

    if (CartCrossSell.hasRealSlides(container)) {
      container.hidden = false;

      if (previousKey === productIdsKey) {
        container.dataset.loadedProductIds = productIdsKey;
        CartCrossSell.initSlider(container);
        return;
      }

      delete container.dataset.loadedProductIds;
      CartCrossSell.loadRecommendations(container, { keepContent: true });
      return;
    }

    delete container.dataset.loadedProductIds;
    CartCrossSell.loadRecommendations(container);
  }

  static async syncCartProductIds() {
    try {
      const cart = await fetch(`${window.routes?.cart_url || "/cart"}.js`).then(
        (response) => response.json(),
      );
      const productIds = [
        ...new Set(cart.items.map((item) => String(item.product_id))),
      ].join(",");

      document.querySelectorAll(".cart-cross-sell").forEach((container) => {
        if (container.dataset.context === "product-drawer") return;
        if (container.dataset.productIds === productIds) return;
        CartCrossSell.syncContainer(container, productIds);
      });
    } catch (e) {
      console.error(e);
    }
  }

  static async loadRecommendations(container, options = {}) {
    if (!container) return;

    const productIdsKey = container.dataset.productIds || "";
    if (!productIdsKey) {
      container.hidden = true;
      return;
    }

    if (
      container.dataset.loadedProductIds === productIdsKey &&
      CartCrossSell.hasRealSlides(container)
    ) {
      CartCrossSell.initQuickAdd(container);

      if (CartCrossSell.getSwiper(container)) {
        CartCrossSell.updateSlider(container);
        if (!container.classList.contains("cart-cross-sell--slider-ready")) {
          CartCrossSell.finishSliderInit(container);
        }
      } else {
        CartCrossSell.initSlider(container);
      }

      if (
        container.dataset.context === "product-drawer" &&
        container.classList.contains("cart-cross-sell--slider-ready")
      ) {
        container.dispatchEvent(
          new CustomEvent("cross-sell:loaded", { bubbles: true }),
        );
      }
      return;
    }

    if (CartCrossSell.pendingLoads.has(container)) return;

    const productIds = productIdsKey.split(",").filter(Boolean);
    const cartProductIdSet = new Set(productIds);
    const cachedSlides = CartCrossSell.getCachedSlides(container, productIdsKey);
    const hasExistingContent = CartCrossSell.hasRealSlides(container);

    if (cachedSlides?.length) {
      const filteredSlides = CartCrossSell.filterSlidesForCart(
        cachedSlides,
        cartProductIdSet,
        container,
      );

      if (filteredSlides.length > 0) {
        CartCrossSell.applySlides(container, filteredSlides, productIdsKey);
        return;
      }

      CartCrossSell.cache.delete(
        CartCrossSell.getCacheKey(container, productIdsKey),
      );
    }

    const loadToken = Symbol();
    CartCrossSell.loadTokens.set(container, loadToken);
    CartCrossSell.pendingLoads.add(container);

    if (!hasExistingContent && !options.keepContent) {
      // No existing slides yet: keep the container hidden while recommendations
      // load, then reveal it in applySlides once real slides are ready.
      container.hidden = true;
      CartCrossSell.setLoadingState(container, true);
    } else {
      container.hidden = false;
      container.classList.toggle(
        "cart-cross-sell--has-content",
        hasExistingContent || options.keepContent,
      );
      CartCrossSell.setLoadingState(container, true);
    }

    const maxProducts = parseInt(container.dataset.maxProducts || "15", 10);
    const sectionId = container.dataset.sectionId;
    const baseUrl = container.dataset.url;
    const seenIds = new Set();
    const slides = [];

    try {
      const results = await Promise.all(
        productIds.map(async (productId) => {
          const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}`;
          const response = await fetch(url);
          const text = await response.text();
          const html = document.createElement("div");
          html.innerHTML = text;
          return Array.from(html.querySelectorAll(".cart-cross-sell__slide"));
        }),
      );

      if (CartCrossSell.loadTokens.get(container) !== loadToken) return;

      for (const slideList of results) {
        for (const slide of slideList) {
          const id = slide.dataset.productId;
          if (cartProductIdSet.has(id)) continue;
          if (seenIds.has(id)) continue;
          seenIds.add(id);
          slides.push(slide);
          if (slides.length >= maxProducts) break;
        }
        if (slides.length >= maxProducts) break;
      }

      CartCrossSell.applySlides(container, slides, productIdsKey);
    } catch (e) {
      console.error(e);
      if (CartCrossSell.loadTokens.get(container) !== loadToken) return;

      if (!CartCrossSell.hasRealSlides(container)) {
        container.hidden = true;
      }

      container.classList.remove(
        "cart-cross-sell--pending-slider",
        "cart-cross-sell--slider-ready",
      );
      CartCrossSell.setLoadingState(container, false);
    } finally {
      if (CartCrossSell.loadTokens.get(container) === loadToken) {
        CartCrossSell.pendingLoads.delete(container);
      }
    }
  }

  static initQuickAdd(container) {
    if (!container || container.dataset.quickAddDelegated === "true") return;

    container.dataset.quickAddDelegated = "true";

    container.addEventListener("click", (event) => {
      const optionsBtn = event.target.closest(
        ".cart-cross-sell-card__quick-add",
      );
      if (!optionsBtn || !container.contains(optionsBtn)) return;

      event.preventDefault();

      const card = optionsBtn.closest(".cart-cross-sell-card");
      const quickAdd = card?.querySelector(".card__quick-add");
      if (!quickAdd) return;

      if (!optionsBtn.classList.contains("active")) {
        container
          .querySelectorAll(".card__quick-add.open")
          .forEach((panel) => panel.classList.remove("open"));
        container
          .querySelectorAll(".cart-cross-sell-card__quick-add.active")
          .forEach((btn) => btn.classList.remove("active"));
      }

      quickAdd.classList.toggle("open");
      optionsBtn.classList.toggle("active");
    });

    container.addEventListener("change", (event) => {
      const input = event.target.closest(
        '.card__size-options input[type="radio"]',
      );
      if (!input || !container.contains(input)) return;

      const sizeOptions = input.closest(".card__size-options");
      const variantInput = sizeOptions?.previousElementSibling;
      if (!variantInput?.classList.contains("product-variant-id")) return;

      variantInput.value = input.value;
      sizeOptions.nextElementSibling?.click();
    });
  }

  static watchDrawer() {
    document.querySelectorAll("cart-drawer, product-info-drawer").forEach((drawer) => {
      if (drawer.dataset.crossSellWatcher === "true") return;

      drawer.dataset.crossSellWatcher = "true";

      const onActive = () => {
        if (!drawer.classList.contains("active")) return;

        // Drawer just became visible — re-measure sliders and re-check loads.
        drawer.querySelectorAll(".cart-cross-sell").forEach((container) => {
          CartCrossSell.updateSlider(container);
        });

        CartCrossSell.scheduleInit();
      };

      const observer = new MutationObserver(onActive);
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ["class"],
      });

      if (drawer.matches("cart-drawer")) {
        document
          .querySelector("#cart-icon-bubble")
          ?.addEventListener("click", onActive);
      }
    });
  }
}

window.CartCrossSell = CartCrossSell;

function initCartCrossSell() {
  if (window.__cartCrossSellInitialized) return;
  window.__cartCrossSellInitialized = true;

  CartCrossSell.watchDrawer();
  CartCrossSell.init();

  document.querySelectorAll("product-info-drawer").forEach((drawer) => {
    CartCrossSell.preloadProductDrawerCrossSell(drawer);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCartCrossSell);
} else {
  initCartCrossSell();
}

if (typeof subscribe === "function" && !window.__cartCrossSellSubscribed) {
  window.__cartCrossSellSubscribed = true;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === "cart-items") return;

    window.setTimeout(async () => {
      await CartCrossSell.syncCartProductIds();
      CartCrossSell.scheduleInit();
    }, 0);
  });
}
})();
